// 1. Framework / library imports
import { memo, useCallback, useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";

// 3. UI Components
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Dialog } from "ui/components/Dialog";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import HorizontalScroll from "ui/components/Scroll/HorizontalScroll/HorizontalScroll";

// 4. Local Types
import { PictureGalleryPreviewProps, PictureItem } from "./types";

const SortableThumbnail: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        touchAction: "none",
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
};

const PictureGalleryPreview = <T extends PictureItem>(props: PictureGalleryPreviewProps<T>) => {
    const {
        show,
        pictures,
        initialSelectedId,
        isLoading: externalLoading = false,
        allowDelete = false,
        onDelete,
        allowReorder = false,
        onReorder,
        onClose,
        onSave,
        deleteDialogTitle,
        deleteDialogMessage,
        deleteDialogConfirmText,
        deleteDialogCancelText,
    } = props;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id || !onReorder) return;

        const oldIndex = displayPictures.findIndex((p) => p.id === active.id);
        const newIndex = displayPictures.findIndex((p) => p.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const previous = displayPictures;
        const reordered = arrayMove(displayPictures, oldIndex, newIndex);
        setDisplayPictures(reordered);

        try {
            await onReorder(reordered.map((p) => p.id));
        } catch {
            setDisplayPictures(previous);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onReorder]);

    const { t } = useTranslate();
    const [selectedPicture, setSelectedPicture] = useState<T | undefined>();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState<string>("");
    const [pendingDeletes, setPendingDeletes] = useState<string[]>([]);
    const [displayPictures, setDisplayPictures] = useState<T[]>(pictures);

    useEffect(() => {
        setDisplayPictures(pictures);
    }, [pictures]);

    useEffect(() => {
        if (initialSelectedId && displayPictures.length > 0) {
            const found = displayPictures.find(pic => pic.id === initialSelectedId);
            setSelectedPicture(found);
        } else if (displayPictures.length > 0 && !selectedPicture) {
            setSelectedPicture(displayPictures[0]);
        }
    }, [initialSelectedId, displayPictures]);

    const handleThumbnailClick = useCallback((picture: T) => {
        setSelectedPicture(picture);
    }, []);

    const handlePrev = useCallback(() => {
        if (!selectedPicture || displayPictures.length === 0) return;
        const currentIndex = displayPictures.findIndex(p => p.id === selectedPicture.id);
        const prevIndex = (currentIndex - 1 + displayPictures.length) % displayPictures.length;
        setSelectedPicture(displayPictures[prevIndex]);
    }, [selectedPicture, displayPictures]);

    const handleNext = useCallback(() => {
        if (!selectedPicture || displayPictures.length === 0) return;
        const currentIndex = displayPictures.findIndex(p => p.id === selectedPicture.id);
        const nextIndex = (currentIndex + 1) % displayPictures.length;
        setSelectedPicture(displayPictures[nextIndex]);
    }, [selectedPicture, displayPictures]);

    useEffect(() => {
        if (!show) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") handlePrev();
            else if (e.key === "ArrowRight") handleNext();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [show, handlePrev, handleNext]);

    const handleDeleteClick = useCallback((pictureId: string) => {
        setSelectedDeleteId(pictureId);
        setOpenDialog(true);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (!selectedDeleteId) return;

        setDisplayPictures(prev => {
            const updated = prev.filter(pic => pic.id !== selectedDeleteId);

            if (selectedPicture?.id === selectedDeleteId) {
                setSelectedPicture(updated[0]);
            }

            return updated;
        });

        setPendingDeletes(prev => [...prev, selectedDeleteId]);
        setSelectedDeleteId("");
        setOpenDialog(false);
    }, [selectedDeleteId, selectedPicture]);

    const handleDeleteCancel = useCallback(() => {
        setSelectedDeleteId("");
        setOpenDialog(false);
    }, []);

    const handleSave = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (pendingDeletes.length === 0) return;

        if (onDelete) {
            await onDelete(pendingDeletes);
        }

        setPendingDeletes([]);
        setSelectedDeleteId("");

        if (onSave) {
            onSave();
        }
    }, [pendingDeletes, onDelete, onSave]);

    const renderThumbnail = (picture: T) => (
        <div
            key={picture.id}
            className="relative border cursor-pointer bg-slate-50 border-gray-200 rounded flex justify-center items-center max-h-full h-32 w-32 group"
        >
            {picture.url && (
                <>
                    <img
                        src={picture.url}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        onClick={() => handleThumbnailClick(picture)}
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/60 transition-all duration-300 pointer-events-none"></div>
                </>
            )}
            {allowDelete && (
                <span
                    className="absolute top-3 right-3 bg-white/80 border-2 border-red-300 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white z-10"
                    onClick={() => handleDeleteClick(picture.id)}
                >
                    <ReactSVG
                        src="/media/icons/close.svg"
                        className="w-4 h-4 block rounded-full stroke-red-700 stroke-2"
                    />
                </span>
            )}
        </div>
    );

    return (
        <Modal show={show} onHide={onClose} size="full">
            <ModalHeader onHide={onClose} />
            <div className="h-screen w-full m-auto">
                <div className="h-full">
                    <form noValidate className="text-left flex flex-col justify-between h-full pb-36">
                        <div className="flex-1 flex justify-center items-center relative">
                            {selectedPicture && (
                                <img
                                    src={selectedPicture.url}
                                    alt=""
                                    className="max-w-full max-h-full object-contain rounded"
                                />
                            )}
                            {displayPictures.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={handlePrev}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-slate-50 rounded-md w-20 h-20 flex items-center justify-center ring-1 ring-slate-200 shadow-sm transition"
                                        aria-label="Previous"
                                    >
                                        <span className="text-gray-700 text-3xl leading-none">&#8249;</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleNext}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-slate-50 rounded-md w-20 h-20 flex items-center justify-center ring-1 ring-slate-200 shadow-sm transition"
                                        aria-label="Next"
                                    >
                                        <span className="text-gray-700 text-3xl leading-none">&#8250;</span>
                                    </button>
                                </>
                            )}
                        </div>
                        {displayPictures.length > 0 && (
                            <HorizontalScroll className="mx-auto">
                                <div className="flex flex-row gap-2 justify-center pt-4">
                                    {allowReorder ? (
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={displayPictures.map((p) => p.id)}
                                                strategy={horizontalListSortingStrategy}
                                            >
                                                {displayPictures.map((picture) => (
                                                    <SortableThumbnail key={picture.id} id={picture.id}>
                                                        {renderThumbnail(picture)}
                                                    </SortableThumbnail>
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                    ) : (
                                        displayPictures.map((picture) => (
                                            <div key={picture.id}>{renderThumbnail(picture)}</div>
                                        ))
                                    )}
                                </div>
                            </HorizontalScroll>
                        )}
                        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                            <Button
                                variant="outlined"
                                children={t("admin.close")}
                                onClick={() => onClose()}
                            />
                            {allowDelete && pendingDeletes.length > 0 && (
                                <Button
                                    variant="primary"
                                    children={t("admin.save")}
                                    onClick={handleSave}
                                />
                            )}
                        </div>
                    </form>
                </div>
            </div>

            <Dialog
                open={openDialog}
                title={deleteDialogTitle ?? t("admin.dialog.delete.title")}
                message={deleteDialogMessage ?? t("admin.dialog.delete.message")}
                confirmText={deleteDialogConfirmText ?? t("admin.dialog.delete.confirm")}
                cancelText={deleteDialogCancelText ?? t("admin.dialog.delete.cancel")}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
            <Loader isLoading={externalLoading}> </Loader>
        </Modal>
    );
};

export default memo(PictureGalleryPreview) as typeof PictureGalleryPreview;
